import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { ApiContext } from '@api/api';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import SvgCoStar from '@icons/CoStar.tsx';
import ExpeditionPrefillCheckboxes from '@components/RingilComponents/ExpeditionPrefillCheckboxes.tsx';
import {
  PrefillOptions,
  useExpeditionPrefillDefaults,
} from '@components/RingilComponents/expeditionPrefillOptions.ts';
import { stampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  onSelect: (entityId: string, prefillOptions: PrefillOptions, isTemplate: boolean) => void;
}

interface ExpeditionOption {
  label: string;
  value: string;
  isFavorite?: boolean;
}

interface TemplateOption {
  label: string;
  value: string;
}

export const useCreatePrefillFromFavoritesSidebar = (editDistributionAllowed: boolean) => {
  const [visible, setVisible] = useState(false);

  const PrefillFromFavoritesSidebar: React.FC<ComponentProps> = ({ onSelect }) => {
    const { tr } = useTranslationLgs();
    const {
      searchExpeditionByNameOrAppId,
      searchTemplateExpeditionByName,
    } = useContext(ApiContext);
    const [isLoading, setIsLoading] = useState(false);
    const [templateSearchEnabled, setTemplateSearchEnabled] = useState(false);
    const [options, setOptions] = useState<ExpeditionOption[]>([]);
    const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
    const [selectedExpedition, setSelectedExpedition] = useState<string>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<string>(null);

    const expeditionDropdownWrapperRef = useRef<HTMLDivElement | null>(null);
    const templateDropdownWrapperRef = useRef<HTMLDivElement | null>(null);
    const [prefillOptions, setPrefillOptions] = useState<PrefillOptions>({
      routeAndParameters: true,
      cargoAndOrders: true,
      labels: true,
      internalInfo: true,
      documents: true,
      carrierSelection: editDistributionAllowed,
      carrierNote: true,
    });

    const settingsDefaults = useExpeditionPrefillDefaults();
    useEffect(() => {
      if (settingsDefaults) setPrefillOptions(settingsDefaults);
    }, [settingsDefaults]);

    const updatePrefillOptions = (patch: Partial<PrefillOptions>) => {
      setPrefillOptions(prev => ({ ...prev, ...patch }));
    };

    useEffect(() => {
      if (visible) {
        searchExpeditions({ filter: '' });
        searchTemplates({ filter: '' });
      }
    }, [visible, searchExpeditionByNameOrAppId]);

    useEffect(() => {
      if (!visible) return;
      stampDataTestIdOnDescendantRef(expeditionDropdownWrapperRef, '.p-dropdown', 'prefillFromExpedition');
      stampDataTestIdOnDescendantRef(templateDropdownWrapperRef, '.p-dropdown', 'prefillFromTemplate');
    }, [visible, templateSearchEnabled, options.length, templateOptions.length]);

    const searchExpeditions = (event: { filter: string }) => {
      const query = event.filter || '';

      setIsLoading(true);
      searchExpeditionByNameOrAppId(
        query,
        (data) => {
          const expeditionOptions = data.expeditions.map(expedition => ({
            label: expedition.name
              ? (expedition.applicationId ? `${expedition.name} | ${expedition.applicationId}` : expedition.name)
              : (expedition.applicationId || ''),
            value: expedition.id,
            isFavorite: expedition.isFavorite || false,
          }));
          setOptions(expeditionOptions);
          setIsLoading(false);
        },
        null,
        null,
      );
    };

    const searchTemplates = (event: { filter: string }) => {
      const query = event.filter || '';

      setIsLoading(true);
      searchTemplateExpeditionByName(
        query,
        (data) => {
          const templateOptions = data.templates.map(template => ({
            label: template.name || '',
            value: template.id,
          }));
          setTemplateOptions(templateOptions);
          if (!query && templateOptions.length > 0) {
            setTemplateSearchEnabled(true);
          }
          setIsLoading(false);
        },
        null,
        null,
      );
    };

    const handleContinue = () => {
      if (selectedExpedition) {
        onSelect(selectedExpedition, prefillOptions, false);
        setVisible(false);
      } else if (selectedTemplate) {
        onSelect(selectedTemplate, prefillOptions, true);
        setVisible(false);
      }
    };

    const handleExpeditionChange = (e: { value: string }) => {
      setSelectedExpedition(e.value);
      if (e.value) {
        setSelectedTemplate(null);
      }
    };

    const handleTemplateChange = (e: { value: string }) => {
      setSelectedTemplate(e.value);
      if (e.value) {
        setSelectedExpedition(null);
      }
    };


    const itemTemplate = (option: ExpeditionOption) => {
      return (
        <div
          className="flex items-center justify-between w-full"
          data-testid={`prefillFromExpedition-${toTestIdSlug(option.label)}`}
        >
          <span>{option.label}</span>
          {option.isFavorite && (
            <div className="flex-shrink-0 ml-4">
              <SvgCoStar className="text-yellow-400 fill-current" />
            </div>
          )}
        </div>
      );
    };

    const selectedItemTemplate = (option: ExpeditionOption) => {
      if (!option) {
        return <span>{tr('PrefillFromFavoritesSidebar.searchExpeditions', 'Search expeditions')}</span>;
      }

      return itemTemplate(option);
    };

    const templateItemTemplate = (option: TemplateOption) => {
      return (
        <div
          className="flex items-center justify-between w-full"
          data-testid={`prefillFromTemplate-${toTestIdSlug(option.label)}`}
        >
          <span>{option.label}</span>
        </div>
      );
    };

    const selectedTemplateItemTemplate = (option: TemplateOption) => {
      if (!option) {
        return <span>{tr('PrefillFromFavoritesSidebar.searchTemplates', 'Search templates')}</span>;
      }

      return templateItemTemplate(option);
    };

    if (!visible) return null;

    return (
      <RingilSidebar
        visible={visible}
        onBack={() => setVisible(false)}
        onContinue={handleContinue}
        heading={tr('PrefillFromFavoritesSidebar.prefillFromFavorites', 'Prefill from favorites')}
        loading={isLoading}
        continueLabel={tr('PrefillFromFavoritesSidebar.continueLabel', 'Pre-fill')}
        dataTestIdContinue="prefillSave"
        dataTestIdBack="prefillClose"
      >
        <div className={'flex flex-col gap-4'}>
          <h3
            className="text-lg font-medium mb-2">{tr('PrefillFromFavoritesSidebar.fromExpedition', 'From expedition')}</h3>
          <div ref={expeditionDropdownWrapperRef}>
            <Dropdown
              value={selectedExpedition}
              options={options}
              onChange={handleExpeditionChange}
              filter
              showFilterClear
              filterBy="label"
              placeholder={tr('PrefillFromFavoritesSidebar.searchExpeditions', 'Search expeditions')}
              onFilter={searchExpeditions}
              className="w-full"
              itemTemplate={itemTemplate}
              valueTemplate={selectedItemTemplate}
              filterInputAutoFocus
            />
          </div>

          {templateSearchEnabled && (
            <>
              <h3
                className="text-lg font-medium mb-2 mt-4">{tr('PrefillFromFavoritesSidebar.fromTemplate', 'From template')}</h3>
              <div ref={templateDropdownWrapperRef}>
                <Dropdown
                  value={selectedTemplate}
                  options={templateOptions}
                  onChange={handleTemplateChange}
                  filter
                  showFilterClear
                  filterBy="label"
                  placeholder={tr('PrefillFromFavoritesSidebar.searchTemplates', 'Search templates')}
                  onFilter={searchTemplates}
                  className="w-full"
                  itemTemplate={templateItemTemplate}
                  valueTemplate={selectedTemplateItemTemplate}
                  filterInputAutoFocus
                />
              </div>
            </>
          )}


          {(selectedExpedition || selectedTemplate) && (
            <div className="mt-4">
              <h3
                className="text-lg font-medium mb-3">{tr('PrefillFromFavoritesSidebar.whatToPrefill', 'What to prefill')}</h3>

              <ExpeditionPrefillCheckboxes
                value={prefillOptions}
                onChange={updatePrefillOptions}
                editDistributionAllowed={editDistributionAllowed}
                isSourceLinkedToSn={false}
              />
            </div>
          )}
        </div>
      </RingilSidebar>
    )
      ;
  };

  return { PrefillFromFavoritesSidebar, setVisible, visible };
};
